/**
 * Abas da página de Configurações, agrupadas. Fonte única: a página valida o
 * `?tab=` por aqui e o dropdown "Configurações" do menu do topo monta os
 * submenus a partir desta lista. Sem menu lateral na página (rebrand,
 * out/2026), o dropdown é a ÚNICA navegação — aba nova entra aqui e aparece
 * lá sozinha.
 */
export type TabId =
  | 'quick-replies'
  | 'closing-reasons'
  | 'holidays'
  | 'tags'
  | 'templates'
  | 'media-library'
  | 'avaliacao'
  | 'users'
  | 'departments'
  | 'access-groups'
  | 'access-profiles'
  | 'custom-fields'
  | 'lgpd'
  | 'webhooks'
  | 'push'
  | 'api-keys'
  | 'api-docs';

export interface SecaoConfig {
  label: string;
  /** soAdmin: aba fora do alcance do perfil com Configurações (a API recusa). */
  items: Array<{ id: TabId; label: string; soAdmin?: true }>;
}

export const SECOES_CONFIG: SecaoConfig[] = [
  {
    label: 'Atendimento',
    items: [
      { id: 'templates', label: 'Templates HSM' },
      { id: 'media-library', label: 'Galeria de Mídia' },
      { id: 'avaliacao', label: 'Avaliação' },
    ],
  },
  {
    label: 'Cadastros',
    items: [
      { id: 'users', label: 'Usuários' },
      { id: 'access-profiles', label: 'Perfis de Acesso' },
      { id: 'departments', label: 'Departamentos' },
      { id: 'access-groups', label: 'Grupos de Acesso' },
      { id: 'custom-fields', label: 'Campos Customizados' },
      { id: 'holidays', label: 'Feriados' },
      { id: 'quick-replies', label: 'Mensagens Rápidas' },
      { id: 'closing-reasons', label: 'Motivos de Fechamento' },
      { id: 'tags', label: 'Etiquetas' },
    ],
  },
  {
    label: 'Integrações',
    items: [
      { id: 'webhooks', label: 'Webhooks' },
      { id: 'push', label: 'Push' },
      // Chave de API e LGPD: só admin (decisão do dono, 29/out/2026 — vide
      // REGRAS em apps/api/src/shared/lib/perfil-de-acesso.ts).
      { id: 'api-keys', label: 'Chave de API', soAdmin: true },
      { id: 'api-docs', label: 'Documentação' },
    ],
  },
  {
    label: 'Conformidade',
    items: [{ id: 'lgpd', label: 'LGPD', soAdmin: true }],
  },
];

/** As abas que este usuário pode abrir; grupo que fica vazio some. */
export function secoesVisiveis(ehAdmin: boolean): SecaoConfig[] {
  if (ehAdmin) return SECOES_CONFIG;
  return SECOES_CONFIG.map((s) => ({ ...s, items: s.items.filter((i) => !i.soAdmin) })).filter(
    (s) => s.items.length > 0,
  );
}

const ABAS_SO_ADMIN: ReadonlySet<TabId> = new Set(
  SECOES_CONFIG.flatMap((s) => s.items.filter((i) => i.soAdmin).map((i) => i.id)),
);

export function abaSoAdmin(t: TabId): boolean {
  return ABAS_SO_ADMIN.has(t);
}

export const DEFAULT_TAB: TabId = 'holidays';

const VALID_TAB_IDS: ReadonlySet<TabId> = new Set(
  SECOES_CONFIG.flatMap((s) => s.items.map((i) => i.id)),
);

export function isValidTab(t: unknown): t is TabId {
  return typeof t === 'string' && VALID_TAB_IDS.has(t as TabId);
}
